import { CONTROL_ERRORS } from '$core/tokens/control-errors.token';
import { transition, trigger, useAnimation } from '@angular/animations';
import {
  Component,
  Host,
  Inject,
  Input,
  OnInit,
  Optional,
} from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  FormGroupDirective,
  FormGroupName,
  NgForm,
} from '@angular/forms';
import { fadeInDown } from 'ng-animate';

@Component({
  selector: 'app-control-errors',
  templateUrl: './control-errors.component.html',
  styles: [
    `
      :host {
        flex-basis: 100%;
      }
      .error {
        display: block;
        margin-top: 0.5rem;
        font-size: 14px;
        line-height: inherit;
      }

      .error--standalone {
        margin-bottom: 0.5rem;
        text-align: center;
      }
    `,
  ],
  animations: [
    trigger('fadeInDown', [
      transition(':enter', useAnimation(fadeInDown), {
        params: { timing: 0.2 },
      }),
    ]),
  ],
})
export class ControlErrorsComponent implements OnInit {
  @Input() public control: string;
  @Input() public controlObj: AbstractControl;
  @Input() public errors: Object;

  public form: NgForm | FormGroupName | FormGroupDirective;

  constructor(
    @Optional() @Host() private ngForm: NgForm,
    @Optional() @Host() private formGroupName: FormGroupName,
    @Optional() @Host() private formGroupDirective: FormGroupDirective,
    @Inject(CONTROL_ERRORS) private controlErrors: Record<string, string>
  ) {
    if (!this.ngForm && !this.formGroupName && !formGroupDirective) {
      throw new TypeError(
        'control-errors must be used with a parent ngForm or formGroup directive'
      );
    }
  }

  public ngOnInit() {
    this.form = this.ngForm || this.formGroupName || this.formGroupDirective;
  }

  public get error() {
    let control: AbstractControl | ControlContainer;

    if (this.form instanceof NgForm) {
      if (!this.form.submitted) {
        return false;
      }

      control = this.form.controls[this.control];
    }

    if (
      this.form instanceof FormGroupName ||
      this.form instanceof FormGroupDirective
    ) {
      control = this.form.control.get(this.control);
      if (control && control.untouched && !control.root['submitted']) {
        return false;
      }
    }

    if (!control) {
      control = this.controlObj || this.form;
    }

    let firstError;
    Object.keys(this.controlErrors).some((err) => {
      if (control.hasError(err)) {
        firstError = this.normalizeError(
          control.errors[err],
          err,
          this.controlErrors[err]
        );
        return true;
      }
    });
    return firstError;
  }

  private normalizeError(
    controlError: Object,
    type: string,
    error: string
  ): string {
    let normalizedError: string = error;

    switch (type) {
      case 'serverError':
        if (Array.isArray(controlError)) {
          normalizedError = controlError.join(', ').trim();
        }
        if (typeof controlError === 'string') {
          normalizedError = controlError;
        }
        break;
    }

    return normalizedError;
  }
}
